
import { CustomExercise, Exercise, ExerciseDbService, ExerciseSlot, getExerciseSlotShortId, isCustomExerciseById } from 'services/exercise-db.service';
import { DocWatcher } from 'services/database-wrapper';
// re-export the lineage walker so existing call sites
// (`from 'services/exercise-switching'`) keep working unchanged.
export { findNearestLiveAncestorAsync } from './exercise-lineage';
// the program-backup restore moved out to a file with no Angular in its import chain, so
// unit tests can reach it. Re-exported for the same reason as the line above (#5113).
export { backupUsedSubSlot, restoreSlotFromBackupIfValid, restoreTrainingProgram, RestoredSlotCarry } from './program-backup-restore';
import { backupUsedSubSlot, restoreSlotFromBackupIfValid } from './program-backup-restore';
import { DateTime } from 'luxon';
import { ExerciseIntensities, FitnessPlan, IntensityLevel, Plan_SubSlot, constructSubslotIdFromParts, findSubslotIdHoldingExercise, getSubslotId, workoutAlreadyListsExercise, parseSubslotIdAndOffset } from '@cf-shared/data-model/fitness-plan';
import { deepClone } from '@cf-shared/deep-helpers';
import { isPouchNotFound } from '@cf-shared/data-model/pouch';
import { TrainingLevel, minTwoTrainingLevels } from '@cf-shared/data-model/training-level';
import { UserDb } from './user-db.service';
import { allRecordedEquipmentIds } from './exercise-stat-equipment';
import { FitnessState, ExerciseStat } from '@cf-shared/data-model/user-state';
import { Log_Workout, Log_WorkoutExercise } from '@cf-shared/data-model/log-workout';
import { findUsableLayoffBackup, seedProgressionFromBackup } from './exercise-layoff-restore';
import { difference, maxBy, concat, reverse } from 'lodash-es';
import { pickSlotForExercise } from 'util/exercise-slot-lookup';
import { getDefaultIntensities, getIntensityForSubSlot } from '@cf-shared/workout-solver-intensity-constants';
import { getSlotLevelBy } from '@cf-shared/workout-solver-interface';

// find out which exercise id will be suggested for the given slot
// this is mostly used for descriptive purposes
// if an exerciseId is already known, it will try to use that. If it is missing, or if no exerciseId is known, it will suggest the appropriate one for that slot.
// a curated read that FAILED (a timed-out fetch, say) is thrown, never answered as "not in the
// catalog": a stand-in shown for an exercise that merely could not be read for a moment is a
// different exercise from the one in the user's plan (df-c21e11979ef2, df-a89f1ea2145e)
export async function getExerciseForSubSlot(planSubSlot: Plan_SubSlot, exerciseId: string, exerciseDb: ExerciseDbService, userDb: UserDb, fitnessState: FitnessState, plan: FitnessPlan, nowUTC: DateTime, watcher: DocWatcher): Promise<Exercise> {
  // a curated id that is not in the catalog falls through to the custom read below, which
  // answers not-found for a curated id, and a stand-in is picked from the slot
  const readCuratedAsync = (id: string) => exerciseDb.getGlobalExerciseByIdOrThrowAsync(id, watcher);
  let getSuggestedExercise = async (toAvoid: string[]) => {
    // predict which exercise would be suggested when opening the workout
    let slot = await exerciseDb.slotForIdAsync(planSubSlot.slotId, watcher);
    // if slot doesn't exist in DB (e.g. custom exercise with no assigned group), use current exercise
    if (!slot) return planSubSlot.exercise_id;
    let newId = suggestAvailableExercise(
      fitnessState,
      plan,
      slot,
      planSubSlot.startIndex,
      planSubSlot.exercise_id,
      nowUTC,
      toAvoid
    );

    return newId;
  };

  // if it wasn't performed yet
  if (!exerciseId) {
    exerciseId = await getSuggestedExercise([]);
  }

  let exercise = exerciseId && await readCuratedAsync(exerciseId);
  try {
    // if a damaged plan entry left no id at all, there is nothing to read: answer "no exercise"
    // so callers can skip the row, instead of handing the database an id it rejects
    if (!exercise && exerciseId) {
      exercise = await userDb.getCustomExerciseByIdAsync(exerciseId);
    }
  } catch (e) {
    // only a missing custom exercise gets a suggested stand-in. an unreadable database or a
    // programmer error must travel up, the same way the curated read above already lets
    // DatabaseUnavailableError travel (df-15c8fd44cf06)
    if (!isPouchNotFound(e)) throw e;
    exerciseId = await getSuggestedExercise([exerciseId]);
    exercise = exerciseId && await readCuratedAsync(exerciseId);
  }

  return exercise;
}

/**
 * #6063: which of these candidate exercises would train on the equipment being moved away from.
 *
 * Used when the rep ceiling (#5979) switches an exercise because the next weight on its equipment is
 * too big a jump: a replacement on the same equipment lands the user straight back in that problem.
 * A candidate counts when either:
 * - its own assigned equipment (either unit, or the legacy single id) is that equipment; or
 * - it has no equipment assigned yet and its equipment template is the one that equipment was built
 *   from. The first time it is logged, `preselectEquipmentIfNeeded` hands it the most recently used
 *   equipment built from its template, which is this same set.
 *
 * `templateIdOf` answers a candidate's equipment template, or undefined when it has none or could not
 * be read — such a candidate is never avoided, because nothing says it shares the equipment.
 */
export function exercisesOnSameEquipment(
  candidateIds: string[],
  equipmentToAvoid: { id: string; basedOnTemplateId?: string },
  fitnessState: FitnessState,
  templateIdOf: (exerciseId: string) => string | undefined,
): string[] {
  return candidateIds.filter(id => {
    const stat = fitnessState.exerciseStats?.[id];
    const assignedIds = allRecordedEquipmentIds(stat);
    // if the candidate already has equipment of its own, that alone decides
    if (assignedIds.length > 0) {
      return assignedIds.includes(equipmentToAvoid.id);
    }
    // otherwise it will be given equipment from its template, so compare templates
    return !!equipmentToAvoid.basedOnTemplateId && templateIdOf(id) === equipmentToAvoid.basedOnTemplateId;
  });
}

/**
 * #6063: pick a replacement while skipping the candidates on the same equipment, and fall back to
 * the ordinary pick when that leaves nothing — never leave the user without an exercise.
 *
 * `suggest` is the ordinary picker with a list of ids to avoid. When the avoiding pick still hands
 * back the current exercise or one on the same equipment, nothing else was left, so the ordinary
 * pick is taken instead. `mayUseSameEquipment` says whether the answer may still train on the same
 * equipment, which decides the message the user is shown.
 */
export function pickReplacementAvoidingSameEquipment(
  suggest: (toAvoid: string[]) => string,
  currentId: string,
  sameEquipment: string[],
): { exerciseId: string; mayUseSameEquipment: boolean } {
  const exerciseId = suggest(sameEquipment);
  if (exerciseId !== currentId && !sameEquipment.includes(exerciseId)) {
    return { exerciseId, mayUseSameEquipment: false };
  }
  const fallbackId = suggest([]);
  return { exerciseId: fallbackId, mayUseSameEquipment: fallbackId === currentId || sameEquipment.includes(fallbackId) };
}

/** Get the exercise id from a slot, starting from a known exercise id, and finding the one that
 * satisfies the switching/plateauing rules.
 *
 * FitnessState and FitnessPlan are needed to see what has plateaued/switched/already used.
 */
export function suggestAvailableExercise(
  fitnessState: FitnessState,
  fitnessPlan: FitnessPlan,
  slot: ExerciseSlot,
  startIndex: number,
  exerciseId: string,
  now_utc: DateTime,
  exercisesToAvoid: string[] = [],
): string {
  void startIndex;
  // if slot doesn't exist (e.g. custom exercise with no assigned group), keep current exercise
  if (!slot) return exerciseId;
  // safely get the index of the current exercise
  let exerciseIndex = slot.exercises.findIndex(es => es.exerciseId === exerciseId);
  if (exerciseIndex < 0) {
    exerciseIndex = 0;
  }

  // build a list of all exercises used in slots other than this ones
  let usedElsewhereList: string[] = Object.keys(fitnessPlan.subSlots)
    .map(k => fitnessPlan.subSlots[k])
    .filter(s => s.slotId !== slot._id || s.startIndex !== startIndex)
    .map(s => s.exercise_id);

  /*
  Build a list of exercises in this slot that are in the order relative to the current exercise.
  The specification prefers exercises with lower indices, if possible, but always tries to 
  use neighboring exercises if possible. So, the list starts from this exercise, goes up to the start of slot,
  and then the rest is down.
  */
  let exercises = difference(
    slot.exercises.map(e => e.exerciseId),
    exercisesToAvoid
  );
  let prioritizedExercises = concat(
    // first the exercise itself...
    [exerciseId],
    // ... then all before the exercise, but reversed...
    reverse(exercises.slice(0, exerciseIndex)),
    // ...then all after the exercise
    exercises.slice(exerciseIndex + 1),
    // and filter out the ones used in other slots
  ).filter(id => id !== undefined && usedElsewhereList.every(idUsed => idUsed !== id) && !exercisesToAvoid.some(idAvoid => idAvoid === id));

  let isSwitched = (id: string) => ExerciseStat.isSwitched(fitnessState.exerciseStats[id], now_utc);
  let isQuarantined = (id: string) => ExerciseStat.isQuarantined(fitnessState.exerciseStats[id], now_utc);

  let newId: string;

  // first try to find a best one that isn't recently switched nor quarantined
  newId = prioritizedExercises.find(id => !isSwitched(id) && !isQuarantined(id));
  if (newId) {
    return newId;
  }

  // if that fails, at least on that isn't switched
  newId = prioritizedExercises.find(id => !isSwitched(id));
  if (newId) {
    return newId;
  }

  // if all are switched, cycle switched in a LRU fashion (skip the current one, as time calculation can mess this up, since
  // it was probably just marked as switched in this step, and browser timer precision limitations due to security can cause
  // that to seem like the same moment)
  let prioritizedExercisesFiltered =
    now_utc.diff(DateTime.fromISO(fitnessState.exerciseStats[exerciseId].switched_utc)).as('days') < 1
      ? prioritizedExercises.filter(id => id !== exerciseId)
      : prioritizedExercises;
  newId = maxBy(
    prioritizedExercisesFiltered,
    id =>
      (fitnessState.exerciseStats[id] &&
        fitnessState.exerciseStats[id].switched_utc &&
        now_utc.diff(DateTime.fromISO(fitnessState.exerciseStats[id].switched_utc)).as('seconds')) ||
      Number.MAX_SAFE_INTEGER,
  );

  if (newId) {
    return newId;
  }

  // this should never happen, but just in case, choose either the next exercise in line, or just plain keep the same one
  return prioritizedExercises[1] || exerciseId;
}


/** Get the prioritized list of exercises fora a slot, starting from a known exercise id, and sorting them by how well they
 *  satisfies the switching/plateauing rules. 
 *
 *  FitnessState and FitnessPlan are needed to see what has plateaued/switched/already used.
 */
export async function suggestAvailableExercisesAsync(
  exerciseDb: ExerciseDbService,
  fitnessState: FitnessState,
  fitnessPlan: FitnessPlan,
  slot: ExerciseSlot,
  startIndex: number,
  exerciseId: string,
  now_utc: DateTime,
  exercisesToAvoid: string[] = [],
  watcher: DocWatcher,
): Promise<string[]> {
  void startIndex;
  // safely get the index of the current exercise
  let exerciseIndex = slot.exercises.findIndex(es => es.exerciseId === exerciseId);
  if (exerciseIndex < 0) {
    exerciseIndex = 0;
  }

  // build a list of all exercises used in slots other than this ones
  let usedElsewhereList: string[] = Object.keys(fitnessPlan.subSlots)
    .map(k => fitnessPlan.subSlots[k])
    .filter(s => s.slotId !== slot._id || s.startIndex !== startIndex)
    .map(s => s.exercise_id);

  /*
  Build a list of exercises in this slot that are in the order relative to the current exercise.
  The specification prefers exercises with lower indices, if possible, but always tries to 
  use neighboring exercises if possible. So, the list starts from this exercise, goes up to the start of slot,
  and then the rest is down.
  */
  let exercises = difference(
    slot.exercises.map(e => e.exerciseId),
    exercisesToAvoid
  ).filter(id => id !== exerciseId);

  let prioritizedExercises = isCustomExerciseById(exerciseId) ? exercises : concat(
    // first the exercise itself...
    [exerciseId],
    exercises
  );

  let isQuarantined = (id: string) => ExerciseStat.isQuarantined(fitnessState.exerciseStats[id], now_utc);

  let bwExercises: string[] = [];
  for (let id of prioritizedExercises) {
    // the reporting read on purpose: a candidate whose read failed only loses its bodyweight
    // ordering below, which is milder than abandoning the whole suggestion list (df-c21e11979ef2)
    let ex = await exerciseDb.getGlobalExerciseByIdAsync(id, watcher);
    if (ex?.bw_pc) {
      bwExercises.push(id);
    }
  }
  let bwExercisesQuarantinedForWeakness = bwExercises.filter(id => isQuarantined(id) && fitnessState.exerciseStats[id].quarantineReason === 'weakness');

  // first sort by recency of being quarantined, a more recent quarantined are on the most bottom place. 
  prioritizedExercises = prioritizedExercises.sort((a: string, b: string) => {
    let quarantinedA_utc = isQuarantined(a) && fitnessState.exerciseStats[a].quarantined_utc;
    let quarantinedB_utc = isQuarantined(b) && fitnessState.exerciseStats[b].quarantined_utc;
    if (!quarantinedA_utc && !quarantinedB_utc) {
      return 0;
    }
    if (quarantinedA_utc && !quarantinedB_utc) {
      return 1;
    }
    if (!quarantinedA_utc && quarantinedB_utc) {
      return -1;
    }
    if (quarantinedA_utc < quarantinedB_utc) {
      return -1;
    }
    if (quarantinedA_utc > quarantinedB_utc) {
      return 1;
    }
    return 0;
  });

  if (bwExercisesQuarantinedForWeakness.length > 0) {
    prioritizedExercises = prioritizedExercises.sort((a: string, b: string) => {
      if (bwExercises.includes(a) && !bwExercises.includes(b)) {
        return 1;
      }
      if (!bwExercises.includes(a) && bwExercises.includes(b)) {
        return -1;
      }
      return 0;
    });
  }

  prioritizedExercises = prioritizedExercises.sort((a: string, b: string) => {
    if (usedElsewhereList.includes(a) && !usedElsewhereList.includes(b)) {
      return 1;
    }
    if (!usedElsewhereList.includes(a) && usedElsewhereList.includes(b)) {
      return -1;
    }
    return 0;
  });

  return prioritizedExercises;
}

// TEMPORARY! WE NEED PROPER DB ENTRIES FOR PROGRESSIONS!
function getIntensitiesForExercise(exerciseId: string, plan: FitnessPlan, slotMap: Map<string, ExerciseSlot>): ExerciseIntensities {
  // if the exercise exists in any of the slots that already have a subslot in the plan
  for (let subslotId of Object.keys(plan.subSlots)) {
    let subslot = plan.subSlots[subslotId];
    let slot = slotMap.get(subslot.slotId);
    if (slot && slot.exercises.find(e => e.exerciseId === exerciseId)) {
      // use that subslot's progression
      return subslot.intensities;
    }
  }

  // find which slot this exercise belongs to and use slot-specific intensities
  let exerciseSlot = findSlotForExercise(slotMap, exerciseId);
  if (exerciseSlot) {
    let slotShortId = getExerciseSlotShortId(exerciseSlot);
    return getIntensityForSubSlot(slotShortId);
  }

  // fallback for custom exercises or unknown slots
  return getDefaultIntensities();
}

// find the best slot for an exercise (lowest index match), using pre-fetched slots
function findSlotForExercise(slotMap: Map<string, ExerciseSlot>, exerciseId: string): ExerciseSlot | undefined {
  return pickSlotForExercise([...slotMap.values()], exerciseId);
}

// determine the appropriate training level for a slot based on user's overall training levels
function getTrainingLevelForSlot(slotShortId: string, plan: FitnessPlan): TrainingLevel {
  // if this is one of the 5 main lifts with a tracked training level
  if (plan.trainingLevels && plan.trainingLevels[slotShortId]) {
    return plan.trainingLevels[slotShortId];
  }

  // determine if upper or lower body based on slot's levelBy property
  const isLowerBody = getSlotLevelBy(slotShortId) === 'lower';

  if (plan.trainingLevels) {
    if (isLowerBody) {
      return minTwoTrainingLevels([plan.trainingLevels['$squat'], plan.trainingLevels['$rdl']]);
    } else {
      return minTwoTrainingLevels([plan.trainingLevels['$bench_press'], plan.trainingLevels['$chin_up'], plan.trainingLevels['$ohp']]);
    }
  }

  return TrainingLevel.Novice;
}

function createSubslotFresh(newExerciseSlotId: string, newExerciseSlotIndex: number, newExerciseId: string, intensities: ExerciseIntensities, numSets: number, trainingLevel: TrainingLevel) {
  console.assert(newExerciseSlotId.startsWith('slot/$'));
  return {
    slotId: newExerciseSlotId,
    startIndex: newExerciseSlotIndex,
    exercise_id: newExerciseId,
    rebuildPathsForIntensity: IntensityLevel.Initial,
    intensities,
    defaultVolume_sets: numSets,
    trainingLevel,
    exercisePaths: [],
  };
}

// Canonical write site for swapping the exercise on a Plan_SubSlot. Every
// site that mutates subSlot.exercise_id MUST route through this helper so
// exercise-tied path/subslot state cannot leak across the swap. Enforced by
// the no-restricted-syntax ESLint rule. See design-docs/exercise-replacement.md.
export function replaceExerciseInSubSlot(subSlot: Plan_SubSlot, newExerciseId: string): void {
  // eslint-disable-next-line no-restricted-syntax -- canonical write site for subSlot.exercise_id (see design-docs/exercise-replacement.md)
  subSlot.exercise_id = newExerciseId;
  subSlot.exercisePaths = [];
  subSlot.rebuildPathsForIntensity = IntensityLevel.Initial;
  subSlot.lastTimeConcluded_utc = undefined;
  subSlot.plateauWorkoutLog_id = undefined;
  // any swap counts as the user's own unless the app's caller says otherwise, below
  delete subSlot.exerciseIdChosenByApp;
  // #6428: the set-aside record names the exercise swapped out of THIS exercise's place; after any
  // swap it describes a slot history the new exercise has no part in. The app's own plateau and
  // weakness swap writes it again right after this call.
  delete subSlot.setAsideExerciseId;
}

/**
 * The same swap as {@link replaceExerciseInSubSlot}, for a swap the APP makes on its own:
 * a lift replaced after a plateau or a weakness test, or a rotation at workout load away
 * from an avoided, quarantined or deleted custom exercise. It also records the exercise the
 * app chose, so the rebuild warning does not tell the user they customized their program
 * when they did not (df-4e4a729f2772). A swap the user asked for goes through
 * `replaceExerciseInSubSlot` and must never come here.
 */
export function rotateExerciseInSubSlotByApp(subSlot: Plan_SubSlot, newExerciseId: string): void {
  replaceExerciseInSubSlot(subSlot, newExerciseId);
  subSlot.exerciseIdChosenByApp = newExerciseId;
}

// Replace an exercise in an existing subslot with another exercise from the same slot (retaining intensities etc)
function createSubslotForSimilarExercise(subslotOriginal: Plan_SubSlot, newExerciseId: string) {
  // clone original to retain slot-tied state (intensities, defaultVolume_sets, trainingLevel, ...),
  // then route the exercise swap through the canonical helper so all exercise-tied state resets uniformly.
  let newSubSlot = deepClone(subslotOriginal);
  replaceExerciseInSubSlot(newSubSlot, newExerciseId);
  return newSubSlot;
}

/** Seed a freshly-created subslot's progression from the user's log history when the exercise was
 *  trained recently (within the layoff window). No-op — leaving the fresh calibration slot — when
 *  the exercise has no usable recent history (a genuinely new exercise, or one last trained beyond
 *  the retest threshold). This is the durable counterpart to the in-session `oldSubslots` restore:
 *  `fitnessState.oldSubslots` is wiped on every login and the planner never backs up dropped
 *  exercises there, so the only progression record that survives a regeneration + reload is
 *  `Log_Workout.exercises[*].planSubslotBackup` (#3321). */
async function seedFreshSlotFromLogBackupAsync(
  newSubslot: Plan_SubSlot,
  newExerciseId: string,
  userDb: UserDb,
  todayLocal: DateTime,
  getExercises: (w: Log_Workout) => Log_WorkoutExercise[],
): Promise<void> {
  // only seed a still-fresh slot — a same-session oldSubslots restore (above) already won
  if (newSubslot.exercisePaths && newSubslot.exercisePaths.length > 0) {
    return;
  }
  const workouts = await userDb.getWorkoutsForExerciseAsync(newExerciseId);
  if (!workouts || workouts.length < 1) {
    return;
  }
  const found = findUsableLayoffBackup(workouts, newExerciseId, todayLocal, getExercises);
  if (found) {
    seedProgressionFromBackup(newSubslot, found.backup);
  }
}

/** Read every slot once, so neither of the two plan edits below does one database read per
 *  exercise it looks at. */
async function loadSlotMapAsync(exerciseDb: ExerciseDbService, watcher: DocWatcher): Promise<Map<string, ExerciseSlot>> {
  const allSlots = await exerciseDb.getAllSlotsAsync(watcher);
  return new Map(allSlots.map(s => [s._id, s]));
}

/** Build a brand-new subslot for an exercise that is not in the plan yet: work out which slot the
 *  exercise belongs to, find an unused index inside that slot, and take the intensities and
 *  training level from there. */
async function createFreshSubslotForExerciseAsync(
  plan: FitnessPlan,
  newExerciseId: string,
  slotMap: Map<string, ExerciseSlot>,
  userDb: UserDb,
): Promise<Plan_SubSlot> {
  // find which slot the exercise belongs to
  let newExerciseSlot: ExerciseSlot;
  // if this is a custom exercise
  if (isCustomExerciseById(newExerciseId)) {
    // return its first slot
    let customExercise = await userDb.getCustomExerciseByIdAsync(newExerciseId) as CustomExercise;
    const slotId = customExercise.usedInSlots?.[0];
    if (slotId) {
      newExerciseSlot = slotMap.get(slotId);
    }
  } else {
    newExerciseSlot = findSlotForExercise(slotMap, newExerciseId);
  }

  // if custom exercise has no assigned slot, use a generic "custom" slot
  if (!newExerciseSlot) {
    newExerciseSlot = { _id: 'slot/$custom', slotName: 'Custom' } as ExerciseSlot;
  }
  // we will use that slot id...
  let newExerciseSlotShortId = getExerciseSlotShortId(newExerciseSlot);

  // ... but we need to find a suitable index for it (one that is unused).
  // Index 0 is the baseline (muscle-group default, e.g. $biceps$0); indices 100+
  // mark additional same-slot entries. Custom exercises with no muscle slot all
  // pile into the shared $custom slot, so this pool must be unbounded — a hardcoded
  // [0,100..109] ceiling silently broke heavily-customized plans at 11 custom
  // exercises (#3483). Find the first unused index: 0, then 100, 101, … no ceiling.
  let newExerciseSlotIndex = 0;
  if (plan.subSlots[constructSubslotIdFromParts(newExerciseSlotShortId, newExerciseSlotIndex)]) {
    newExerciseSlotIndex = 100;
    while (plan.subSlots[constructSubslotIdFromParts(newExerciseSlotShortId, newExerciseSlotIndex)]) {
      newExerciseSlotIndex++;
    }
  }

  // find the intensities and training level for the new exercise
  let intensities = getIntensitiesForExercise(newExerciseId, plan, slotMap);
  let trainingLevel = getTrainingLevelForSlot(newExerciseSlotShortId, plan);
  let numSets = 1;
  return createSubslotFresh('slot/' + newExerciseSlotShortId, newExerciseSlotIndex, newExerciseId, intensities, numSets, trainingLevel);
}

/** Decide which subslot the new exercise should use, and hand back one that is ready to be put
 *  into the plan. Shared by both plan edits below, because the choice is the same for each: reuse
 *  the subslot the exercise already has somewhere in the plan, otherwise build a fresh one and
 *  seed its progression from whatever record of the exercise survives.
 *
 *  `existingSubslotId` is the caller's already-made lookup, passed in rather than repeated here so
 *  a swap can consult it before offering its own in-place shortcut.
 *
 *  `oldSubslotId` is the subslot being swapped away from, and is undefined on the add path. It is
 *  only read by the in-session `oldSubslots` restore, which is a no-op without it. */
async function resolveSubslotForNewExerciseAsync(
  existingSubslotId: string | undefined,
  plan: FitnessPlan,
  newExerciseId: string,
  oldSubslotId: string | undefined,
  slotMap: Map<string, ExerciseSlot>,
  fitnessState: FitnessState,
  userDb: UserDb,
  todayLocal: DateTime,
): Promise<Plan_SubSlot> {
  // if the new exercise is already in the plan, just use that existing subslot
  if (existingSubslotId) {
    return plan.subSlots[existingSubslotId];
  }

  // no usable existing sub slot, we need to create a new one...
  const newSubslot = await createFreshSubslotForExerciseAsync(plan, newExerciseId, slotMap, userDb);

  // must have been created by now
  if (!newSubslot) {
    throw new Error(`Failed to create a new subslot for exercise ${newExerciseId}`);
  }

  // the slot was newly created (ie. we are not reusing a slot from a different WO), so try to give
  // it back the progression the exercise had before.

  // if the new exercise already has a subslot used _recently enough_ (not enough is if it would have caused a re-test anyway)
  restoreSlotFromBackupIfValid(fitnessState, oldSubslotId, newExerciseId, todayLocal, newSubslot);

  // oldSubslots only survives within a session (wiped on login) and the planner never backs up
  // dropped exercises there, so for a re-add across a regeneration + reload the durable
  // progression record is the user's log history. Seed from it so a recently-trained exercise
  // resumes with the normal layoff deload instead of a fresh strength test (#3321). Guarded
  // inside the helper so a same-session oldSubslots restore (above) still wins. Like that
  // oldSubslots restore, this runs for every freshly-created slot — both an add and a swap
  // into a brand-new slot.
  await seedFreshSlotFromLogBackupAsync(newSubslot, newExerciseId, userDb, todayLocal, w => w.exercises);

  return newSubslot;
}

/**
 * Add an exercise to one workout of the plan.
 *
 * Together with {@link changeExerciseInWorkoutAsync} this replaces the old
 * `switchExerciseAsync_t`, which did both jobs (and a third, removal, that nothing called) behind
 * an `action` argument, so the arguments a caller had to supply depended on a word rather than on
 * the function it called (df-c9ee6db39869). Removal is not here: it goes through
 * `removeSubslotFromWorkout`, and `edit-workouts-page.removeExerciseFromWorkout` is its caller.
 */
export async function addExerciseToWorkoutAsync(
  workoutReference: number,
  newExerciseId: string,
  plan: FitnessPlan, fitnessState: FitnessState, exerciseDb: ExerciseDbService, userDb: UserDb,
  todayLocal: DateTime, watcher: DocWatcher): Promise<{ newSubslotId: string }> {

  // verify inputs first. The types say this much already, but a value that is undefined at run
  // time still reaches here from a picker result, and a clear throw beats a later confusing one.
  if (typeof workoutReference !== 'number' || !newExerciseId) {
    throw new Error(`Invalid inputs to addExerciseToWorkoutAsync(): ${workoutReference}, ${newExerciseId}`);
  }

  let workout = plan.workouts[workoutReference];

  const slotMap = await loadSlotMapAsync(exerciseDb, watcher);

  // read a custom exercise even though adding does not use what it says. The read throws when the
  // document cannot be read, and that refusal is part of what this path does today — dropping it
  // would let an add of an unreadable custom exercise quietly succeed.
  if (isCustomExerciseById(newExerciseId)) {
    await userDb.getCustomExerciseByIdAsync(newExerciseId);
  }

  const existingSubslotId = findSubslotIdHoldingExercise(plan, newExerciseId);
  const newSubslot = await resolveSubslotForNewExerciseAsync(
    existingSubslotId, plan, newExerciseId, undefined, slotMap, fitnessState, userDb, todayLocal);

  const newSubslotId = getSubslotId(newSubslot);

  // add the subslot to the workout — guard against pushing a duplicate id. Re-adding
  // an exercise that already has a subslot referenced by THIS workout would otherwise
  // corrupt subSlotIds with a duplicate entry (root of the duplicate $custom$108 in
  // #3483). Adding a shared subslot to a DIFFERENT workout is unaffected: the id is not
  // yet in that workout's array.
  if (!workout.subSlotIds.includes(newSubslotId)) {
    workout.subSlotIds.push(newSubslotId);
  }
  // add the subslot to the plan if it's not already there
  if (!plan.subSlots[newSubslotId]) {
    plan.subSlots[newSubslotId] = newSubslot;
  }

  stopAvoidingExercisePickedByUser(fitnessState, newExerciseId);

  return { newSubslotId: newSubslotId };
}

/** Who chose the exercise a plan change puts in: decides whether the app's avoid marks are lifted (#6076). */
export enum ExerciseChosenBy {
  User = 'user',
  App = 'app',
}

/**
 * The user put this exercise into their plan by hand, so lift any mark the app set to steer them
 * away from it (#6076). Without this, `skipAvoidedExercises` sees the mark on the next workout load
 * and rotates the slot straight back to another exercise, wiping its progression: a pick made
 * within 7 days of an app switch, or 90 days of a plateau or weakness quarantine, never stuck.
 * Never call it for an exercise the app chose: see `ExerciseChosenBy` on `changeExerciseInWorkoutAsync`.
 */
function stopAvoidingExercisePickedByUser(fitnessState: FitnessState, exerciseId: string): void {
  // if the exercise has no stat there is no mark to lift, and an empty stat must not be created
  const stat = fitnessState.exerciseStats?.[exerciseId];
  if (!stat) return;
  ExerciseStat.unmarkSwitched(stat);
  ExerciseStat.unmarkQuarantined(stat);
}

/**
 * Swap one exercise of a workout for another, keeping whatever progression the new exercise can
 * legitimately carry over. See {@link addExerciseToWorkoutAsync} for why these are two functions.
 *
 * `chosenBy` says whether the user picked the new exercise. Only a user's pick lifts the app's
 * avoid marks on it; the workout page's automatic switch at the maximum weight also comes through
 * here, and its replacement can be an exercise the app is still avoiding (#6076).
 */
export async function changeExerciseInWorkoutAsync(
  workoutReference: number, oldSubslotId: string, oldExerciseId: string,
  newExerciseId: string,
  plan: FitnessPlan, fitnessState: FitnessState, exerciseDb: ExerciseDbService, userDb: UserDb,
  todayLocal: DateTime, watcher: DocWatcher, chosenBy: ExerciseChosenBy): Promise<{ newSubslotId: string }> {

  // verify inputs first — same reason as the add path above.
  if (typeof workoutReference !== 'number' || !oldSubslotId || !oldExerciseId || !newExerciseId) {
    throw new Error(`Invalid inputs to changeExerciseInWorkoutAsync(): ${workoutReference}, ${oldSubslotId}, ${oldExerciseId}, ${newExerciseId}`);
  }

  let workout = plan.workouts[workoutReference];

  const slotMap = await loadSlotMapAsync(exerciseDb, watcher);

  // load the new exercise if it is a custom one
  let customNewExercise: CustomExercise = undefined;
  if (isCustomExerciseById(newExerciseId)) {
    customNewExercise = await userDb.getCustomExerciseByIdAsync(newExerciseId);
  }

  // (this block seems like an unused leftover from when exerciseStats.avoided was used,
  // but not safe to remove because it normalizes the stats object from undefined to empty)
  let exerciseStats = fitnessState.exerciseStats[oldExerciseId];
  if (!exerciseStats) {
    exerciseStats = fitnessState.exerciseStats[oldExerciseId] = {} as ExerciseStat;
  }

  // verify the old exercise is actually in the plan at the expected position
  let oldSubslot = plan.subSlots[oldSubslotId];
  if (!oldSubslot || oldSubslot.exercise_id !== oldExerciseId) {
    throw new Error(`Old subslot doesn't match old exercise: ${oldSubslotId}, ${oldExerciseId}`);
  }
  const oldSubslotIndexInWorkout = workout.subSlotIds.indexOf(oldSubslotId);
  if (oldSubslotIndexInWorkout < 0) {
    throw new Error(`Old subslot ${oldSubslotId} not found in workout ${workoutReference}`);
  }

  let oldSlotId = parseSubslotIdAndOffset(oldSubslotId).parsedSlotId;
  const oldSlot = slotMap.get('slot/' + oldSlotId);

  // find out if it is used in multiple places
  let numUses = 0;
  for (let w of plan.workouts) {
    if (w.subSlotIds.includes(oldSubslotId)) {
      numUses++;
    }
  }
  const oldSubslotIsMultiUsed = numUses > 1;

  // if the new exercise is already in this workout, the change below would reuse its subslot and
  // list it twice here; the save then dropped the copy and the workout silently lost an exercise
  // (#6296). The picker refuses such a pick, so reaching this is a caller bug: refuse before
  // anything is changed or backed up.
  if (workoutAlreadyListsExercise(plan, newExerciseId, workout, oldSubslotId)) {
    throw new Error(`Exercise ${newExerciseId} is already in workout ${workoutReference}`);
  }
  const existingSubslotId = findSubslotIdHoldingExercise(plan, newExerciseId);

  // remember the current state of this exercise
  backupUsedSubSlot(oldSubslotId, plan, fitnessState);

  // ----- by this point we know the old exercise's position, and we've backed it up

  // if the exercise can be in the same slot
  // (note that this is _not_ used if the exercise is already somewhere else in the plan, as determined above)
  if (!existingSubslotId && oldSlot) {
    let isNewExerciseInSameSlot = false;
    if (customNewExercise) {
      isNewExerciseInSameSlot = customNewExercise.usedInSlots.length > 0 && customNewExercise.usedInSlots.includes(oldSlot._id);
    } else {
      // `.some` rather than `.find`: this only ever asked whether such an entry exists, and
      // `oldSlot` is properly typed now that it is a plain local, so a found entry is no longer
      // silently assignable to a boolean.
      isNewExerciseInSameSlot = oldSlot.exercises.some(e => e.exerciseId === newExerciseId);
    }
    if (isNewExerciseInSameSlot) {
      // use the same subslot (this is a simple in-place swap for the same slot, as done when plateauing)
      const inPlaceSubslot = createSubslotForSimilarExercise(plan.subSlots[oldSubslotId], newExerciseId);
      // if the user is swapping back to an exercise this subslot held before, give it back the
      // calibration parked by `backupUsedSubSlot`. Without this, swapping away and straight back
      // threw away a strength test passed days earlier (#4590). Training state only: the in-place
      // swap keeps the live subslot's set count, and a count changed between the two swaps stays.
      restoreSlotFromBackupIfValid(fitnessState, oldSubslotId, newExerciseId, todayLocal, inPlaceSubslot, 'trainingStateOnly');
      // across a login the parked copy is gone, so fall back to the log history, as a swap into
      // a fresh subslot does (#3321). It does nothing when the restore above already won.
      await seedFreshSlotFromLogBackupAsync(inPlaceSubslot, newExerciseId, userDb, todayLocal, w => w.exercises);
      plan.subSlots[oldSubslotId] = inPlaceSubslot;
      // only once the change has gone through, so a read that throws above leaves the marks alone
      if (chosenBy === ExerciseChosenBy.User) {
        stopAvoidingExercisePickedByUser(fitnessState, newExerciseId);
      }
      return { newSubslotId: oldSubslotId };
    }
  }

  const newSubslot = await resolveSubslotForNewExerciseAsync(
    existingSubslotId, plan, newExerciseId, oldSubslotId, slotMap, fitnessState, userDb, todayLocal);

  const newSubslotId = getSubslotId(newSubslot);

  // if only used in this workout, remove the old subslot from the plan
  if (!oldSubslotIsMultiUsed) {
    delete plan.subSlots[oldSubslotId];
  }
  plan.subSlots[newSubslotId] = newSubslot;
  // replace the old subslot in the workout
  workout.subSlotIds[oldSubslotIndexInWorkout] = newSubslotId;

  // if the user picked it, lift the app's avoid marks, as the in-place branch above does
  if (chosenBy === ExerciseChosenBy.User) {
    stopAvoidingExercisePickedByUser(fitnessState, newExerciseId);
  }

  return { newSubslotId: newSubslotId };
}

